import MuiSkeleton, { SkeletonProps as MuiSkeletonProps } from '@mui/material/Skeleton';
import React, { useMemo } from 'react';
import { BpDesignSystem } from '../../types';

type StringWithPercent = string;

const DEFAULT_RANGE = [30, 60] as const;

type WidthProp = boolean | undefined | [number, number] | readonly [number, number];

export type SkeletonProps = BpDesignSystem.Props<
  Omit<MuiSkeletonProps, 'css'> & {
    /*! by default [30,60] */
    randomWidthInPercentage?: WidthProp;
    randomWidthAsNumber?: WidthProp; // by default [30,60]
    inline?: boolean;
  }
>;

export const Skeleton: React.FC<SkeletonProps> = ({
  randomWidthInPercentage: randomWidthInPercentageProp,
  randomWidthAsNumber: randomWidthAsNumberProps,
  width,
  inline,
  style,
  dataTest,
  ...rest
}) => {
  const targetWidth = useMemo(() => {
    if (width) {
      return width;
    }
    if (typeof randomWidthAsNumberProps === 'boolean' || Array.isArray(randomWidthAsNumberProps)) {
      if (typeof randomWidthAsNumberProps === 'boolean') {
        return randomNumber(DEFAULT_RANGE[0], DEFAULT_RANGE[1]);
      }
      return randomNumber(randomWidthAsNumberProps[0], randomWidthAsNumberProps[1]);
    } else if (typeof randomWidthInPercentageProp === 'boolean' || Array.isArray(randomWidthInPercentageProp)) {
      if (typeof randomWidthInPercentageProp === 'boolean') {
        return randomNumberWithPercent(DEFAULT_RANGE[0], DEFAULT_RANGE[1]);
      }
      return randomNumberWithPercent(randomWidthInPercentageProp[0], randomWidthInPercentageProp[1]);
    } else {
      return randomNumber(DEFAULT_RANGE[0], DEFAULT_RANGE[1]);
    }
  }, [randomWidthInPercentageProp, randomWidthAsNumberProps, width]);

  return (
    <MuiSkeleton
      data-test={dataTest}
      {...{
        ...rest,
        width: targetWidth ?? width,
        style: {
          ...style,
          display: inline ? 'inline-block' : undefined
        }
      }}
    />
  );
};

function randomNumber(min: number = 0, max: number = 10): number {
  return Math.floor(Math.random() * (max - min) + min);
}
function randomNumberWithPercent(min: number = 30, max: number = 60): StringWithPercent {
  return `${randomNumber(min, max)}%`;
}
